import { useState, useEffect, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { apiClient } from '../api/client'
import { useAuthStore } from '../store/auth'

// Confidence Badge Component
function ConfidenceBadge({ score }: { score: number }) {
  let colorClass = ''
  let label = ''
  
  if (score >= 95) {
    colorClass = 'bg-green-100 text-green-800 border-green-200'
    label = 'Erinomainen'
  } else if (score >= 70) {
    colorClass = 'bg-yellow-100 text-yellow-800 border-yellow-200'
    label = 'Hyvä'
  } else {
    colorClass = 'bg-red-100 text-red-800 border-red-200'
    label = 'Heikko'
  }
  
  return (
    <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border ${colorClass}`} title={`Luottamus: ${score}% - ${label}`}>
      <span className="font-bold">{score}%</span>
      <span className="text-xs">{label}</span>
    </div>
  )
}

// AI Reconciliation Suggestion type
interface AIReconciliationSuggestion {
  id: string
  transactionId: string
  receiptId: string
  confidenceScore: number
  matchedFields: {
    amount: number
    date: number
    description: number
  }
  status: 'pending' | 'accepted' | 'rejected' | 'auto_accepted'
  createdAt: string
  transaction?: {
    date: string
    amount: number
    description: string | null
    counterparty: string | null
  }
  receipt?: {
    filename: string
    createdAt: string
  }
}

export default function ReconciliationPage() {
  const [searchParams] = useSearchParams()
  const { activeOrg } = useAuthStore()
  const organizationId = activeOrg?.id || searchParams.get('organization_id') || ''
  
  const [activeTab, setActiveTab] = useState<'unmatched' | 'matched' | 'all' | 'ai'>('unmatched')
  const [aiSuggestions, setAiSuggestions] = useState<AIReconciliationSuggestion[]>([])
  const [selectedSuggestions, setSelectedSuggestions] = useState<Set<string>>(new Set())
  const [aiLoading, setAiLoading] = useState(false)
  const [aiGenerating, setAiGenerating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Fetch AI suggestions
  const fetchAiSuggestions = useCallback(async () => {
    if (!organizationId) return
    
    setAiLoading(true)
    try {
      const response = await apiClient.get<{ suggestions: AIReconciliationSuggestion[] }>(
        `/reconciliation/suggestions?organization_id=${organizationId}&status=pending&limit=100`
      )
      setAiSuggestions(response.suggestions || [])
    } catch (err) {
      console.error('Failed to fetch AI suggestions:', err)
    } finally {
      setAiLoading(false)
    }
  }, [organizationId])

  // Fetch on tab change
  useEffect(() => {
    if (activeTab === 'ai' && organizationId) {
      fetchAiSuggestions()
    }
  }, [activeTab, organizationId, fetchAiSuggestions])

  const generateAiSuggestions = async () => {
    if (!organizationId) return
    
    setAiGenerating(true)
    try {
      await apiClient.post('/reconciliation/ai-match', {
        organization_id: organizationId,
        auto_accept_threshold: 95
      })
      await fetchAiSuggestions()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate AI suggestions')
    } finally {
      setAiGenerating(false)
    }
  }

  const acceptAiSuggestion = async (suggestionId: string) => {
    if (!organizationId) return
    try {
      await apiClient.post(`/reconciliation/suggestions/${suggestionId}/accept?organization_id=${organizationId}`, {})
      await fetchAiSuggestions()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to accept')
    }
  }

  const rejectAiSuggestion = async (suggestionId: string) => {
    if (!organizationId) return
    try {
      await apiClient.post(`/reconciliation/suggestions/${suggestionId}/reject?organization_id=${organizationId}`, {})
      await fetchAiSuggestions()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reject')
    }
  }

  const toggleSelection = (id: string) => {
    setSelectedSuggestions(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const bulkAccept = async () => {
    for (const id of selectedSuggestions) await acceptAiSuggestion(id)
    setSelectedSuggestions(new Set())
  }

  const bulkReject = async () => {
    for (const id of selectedSuggestions) await rejectAiSuggestion(id)
    setSelectedSuggestions(new Set())
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Tiliotteiden täsmäytys</h1>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 text-red-800">
          {error}
          <button onClick={() => setError(null)} className="ml-4 text-red-600 hover:text-red-800">Sulje</button>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 mb-4">
        {(['unmatched', 'matched', 'all', 'ai'] as const).map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 text-sm font-medium rounded-lg ${
              activeTab === tab ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            {tab === 'ai' ? `🤖 AI Ehdotukset (${aiSuggestions.length})` : tab}
          </button>
        ))}
      </div>

      {/* AI Suggestions Tab */}
      {activeTab === 'ai' && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h2 className="font-semibold text-gray-900">🤖 AI-Täsmäytysehdotukset</h2>
              <p className="text-sm text-gray-500">Tekoäly analysoi tapahtumat ja ehdottaa täsmäytyksiä</p>
            </div>
            <button
              onClick={generateAiSuggestions}
              disabled={aiGenerating}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-300"
            >
              {aiGenerating ? '⏳ Analysoidaan...' : '🤖 Generoi ehdotukset'}
            </button>
          </div>

          {/* Bulk Actions */}
          {selectedSuggestions.size > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 flex items-center justify-between">
              <span className="text-sm text-blue-800">{selectedSuggestions.size} valittu</span>
              <div className="flex gap-2">
                <button onClick={bulkAccept} className="px-3 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700">✓ Hyväksy</button>
                <button onClick={bulkReject} className="px-3 py-1.5 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700">Hylkää</button>
                <button onClick={() => setSelectedSuggestions(new Set())} className="px-3 py-1.5 text-gray-600 text-sm">Tyhjennä</button>
              </div>
            </div>
          )}

          {/* Suggestions List */}
          {aiLoading ? (
            <div className="text-center py-8 text-gray-500">⏳ Ladataan...</div>
          ) : aiSuggestions.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <div className="text-4xl mb-3">🤖</div>
              <p>Ei AI-ehdotuksia</p>
              <p className="text-sm">Paina "Generoi ehdotukset" analysoidaksesi tapahtumat</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center gap-2 pb-2 border-b">
                <input
                  type="checkbox"
                  checked={selectedSuggestions.size === aiSuggestions.length}
                  onChange={(e) => setSelectedSuggestions(e.target.checked ? new Set(aiSuggestions.map(s => s.id)) : new Set())}
                  className="w-4 h-4"
                />
                <span className="text-sm text-gray-600">Valitse kaikki</span>
              </div>

              {aiSuggestions.map(suggestion => (
                <div key={suggestion.id} className={`p-4 border rounded-lg ${selectedSuggestions.has(suggestion.id) ? 'bg-blue-50 border-blue-300' : 'bg-white border-gray-200'}`}>
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={selectedSuggestions.has(suggestion.id)}
                      onChange={() => toggleSelection(suggestion.id)}
                      className="w-4 h-4 mt-1"
                    />
                    <div className="flex-1">
                      <div className="flex justify-between items-start mb-2">
                        <ConfidenceBadge score={suggestion.confidenceScore} />
                        {suggestion.status === 'auto_accepted' && (
                          <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">Auto-hyväksytty</span>
                        )}
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="p-3 bg-gray-50 rounded-lg">
                          <p className="text-xs text-gray-500 mb-1">Tapahtuma</p>
                          <p className="font-medium">{suggestion.transaction?.counterparty || suggestion.transaction?.description}</p>
                          <p className={`font-semibold ${(suggestion.transaction?.amount || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {(suggestion.transaction?.amount || 0).toFixed(2)} €
                          </p>
                        </div>
                        <div className="p-3 bg-gray-50 rounded-lg">
                          <p className="text-xs text-gray-500 mb-1">Tosite</p>
                          <p className="font-medium">{suggestion.receipt?.filename}</p>
                        </div>
                      </div>

                      <div className="mt-3 p-3 bg-gray-50 rounded-lg text-sm">
                        <p className="font-medium text-gray-700 mb-2">Täsmäytyksen perusteet:</p>
                        <div className="flex gap-4">
                          <span>Summa: {Math.round(suggestion.matchedFields.amount * 100)}%</span>
                          <span>Päivä: {Math.round(suggestion.matchedFields.date * 100)}%</span>
                          <span>Kuvaus: {Math.round(suggestion.matchedFields.description * 100)}%</span>
                        </div>
                      </div>

                      <div className="flex gap-2 mt-3">
                        <button onClick={() => acceptAiSuggestion(suggestion.id)} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm">✓ Hyväksy</button>
                        <button onClick={() => rejectAiSuggestion(suggestion.id)} className="px-4 py-2 text-red-600 hover:text-red-800 text-sm">Hylkää</button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Other tabs would go here */}
      {activeTab !== 'ai' && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center text-gray-500">
          <p>Valitse "AI Ehdotukset" -välilehti nähdäksesi tekoälyn täsmäytysehdotukset</p>
        </div>
      )}
    </div>
  )
}
